import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  ChevronLeft,
  ChevronRight,
  Phone,
  Globe,
  Mail,
  Star,
} from "lucide-react";
import RatingPentagon from "./RatingPentagon";
import { photoUrl } from "../config";

const SLIDE_VARIANTS = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

export default function HotelCardMobile({ hotel, onPhotoClick }) {
  const navigate = useNavigate();
  const [imgIdx, setImgIdx] = useState(0);
  const [slideDir, setSlideDir] = useState(0);
  const touchStart = useRef(null);

  const images =
    hotel.photos?.length > 0
      ? hotel.photos.map((p) => `${photoUrl}/${hotel.photo_url}/hotel/${p}.jpg`)
      : hotel.images || ["/empty.jpg"];
  const total = images.length;

  const prev = (e) => {
    e?.stopPropagation?.();
    setSlideDir(-1);
    setImgIdx((i) => (i - 1 + total) % total);
  };

  const next = (e) => {
    e?.stopPropagation?.();
    setSlideDir(1);
    setImgIdx((i) => (i + 1) % total);
  };

  const selectDot = (e, i) => {
    e.stopPropagation();
    setSlideDir(i > imgIdx ? 1 : -1);
    setImgIdx(i);
  };

  // Свайп по фото
  const handleTouchStart = (e) => {
    touchStart.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  };

  const handleTouchEnd = (e) => {
    if (!touchStart.current || total <= 1) return;
    const dx = e.changedTouches[0].clientX - touchStart.current.x;
    const dy = e.changedTouches[0].clientY - touchStart.current.y;
    touchStart.current = null;
    if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      if (dx > 0) prev();
      else next();
    }
  };

  // Клик по фото — открывает просмотрщик, НЕ переходит по карточке
  const handlePhotoClick = (e) => {
    e.stopPropagation();
    onPhotoClick?.(images, imgIdx);
  };

  // Обработчик клика по карточке
  const handleCardClick = () => {
    if (hotel.url) {
      navigate(hotel.url, { state: { from: "sprav" } });
    }
  };

  // Обработчик клика по кнопке "Выбрать"
  const handleSelectClick = (e) => {
    e.stopPropagation();
    if (hotel.url) {
      navigate(hotel.url, { state: { from: "sprav" } });
    }
  };

  // Обработчики для контактов
  const handleContactClick = (e, type, value) => {
    e.stopPropagation();
    if (type === "phone" && value) {
      window.location.href = `tel:${value}`;
    } else if (type === "email" && value) {
      window.location.href = `mailto:${value}`;
    } else if (type === "website" && value) {
      window.open(value, "_blank");
    }
  };

  return (
    <div
      onClick={handleCardClick}
      className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden flex flex-col cursor-pointer active:scale-[0.99] transition-transform"
    >
      {/* Image with carousel */}
      <div
        className="relative w-full h-52 bg-gray-200 group overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handlePhotoClick}
      >
        <AnimatePresence initial={false} custom={slideDir}>
          <motion.img
            key={imgIdx}
            src={images[imgIdx]}
            alt={hotel.name}
            custom={slideDir}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>

        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-black/30 pointer-events-none" />

        {/* Type badge */}
        <span className="absolute top-3 left-3 bg-white/90 text-gray-700 text-xs font-medium px-2.5 py-1 rounded-full shadow-sm z-10">
          {hotel?.template || "Отель"}
        </span>

        {/* Rating pentagon — top right */}
        <div className="absolute top-2 right-2 z-10">
          {hotel?.meta_rating > 0 && (
            <RatingPentagon rating={hotel.meta_rating} size="md" />
          )}
        </div>

        {/* Name overlay */}
        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/70 to-transparent pointer-events-none">
          <h3 className="font-semibold text-white text-base leading-tight pr-12">
            {hotel.name || `#${hotel.id}`}
          </h3>
          <div className="flex items-center gap-1 text-xs text-white/80 mt-0.5">
            <MapPin className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">
              {hotel.city ? `${hotel.city}, ` : ""}
              {hotel.address?.split(",")?.[0] || ""}
            </span>
          </div>
        </div>

        {/* Carousel navigation */}
        {total > 1 && (
          <>
            {/* Prev button */}
            <button
              onClick={prev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors z-10"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            {/* Next button */}
            <button
              onClick={next}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors z-10"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            {/* Dots indicator */}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 flex gap-1 max-w-[60%] overflow-hidden z-10">
              {images.map((_, i) => {
                const shouldShow =
                  total <= 7 ||
                  i === 0 ||
                  i === total - 1 ||
                  Math.abs(i - imgIdx) <= 2;

                if (!shouldShow) return null;

                return (
                  <button
                    key={i}
                    onClick={(e) => selectDot(e, i)}
                    className={`flex-shrink-0 rounded-full transition-all ${
                      i === imgIdx
                        ? "w-4 h-1.5 bg-white"
                        : "w-1.5 h-1.5 bg-white/50 hover:bg-white/70"
                    }`}
                  />
                );
              })}
            </div>

            {/* Photo counter */}
            <div className="absolute bottom-3 right-3 bg-black/50 text-white text-[10px] px-1.5 py-0.5 rounded-md z-10">
              {imgIdx + 1} / {total}
            </div>
          </>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-3 space-y-3">
        {/* Popular services */}
        {hotel.popular_services?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {hotel.popular_services.slice(0, 4).map((service, idx) => (
              <span
                key={idx}
                className="text-xs text-gray-500 bg-gray-50 px-2 py-1 rounded-full"
              >
                {service}
              </span>
            ))}
          </div>
        )}

        {/* Reviews count */}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            <Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400" />
            <span className="text-sm font-semibold text-gray-900">
              {hotel.meta_rating || 0}
            </span>
          </div>
          <span className="text-xs text-gray-400">
            {hotel.reviews_count || 0} отзывов
          </span>
        </div>

        {/* Contacts and Price */}
        <div className="flex items-center justify-between gap-3">
          {/* Contacts */}
          <div className="flex items-center gap-2">
            {hotel.phone && (
              <button
                onClick={(e) =>
                  handleContactClick(e, "phone", hotel.phone?.[0])
                }
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <Phone className="w-4 h-4" />
              </button>
            )}
            {hotel.website && (
              <button
                onClick={(e) => handleContactClick(e, "website", hotel.website)}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <Globe className="w-4 h-4" />
              </button>
            )}
            {hotel.email && (
              <button
                onClick={(e) => handleContactClick(e, "email", hotel.email)}
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <Mail className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Price */}
          {hotel.min_price > 0 && (
            <div className="flex items-center gap-2">
              <div className="text-right">
                <span className="text-xs text-gray-400">от </span>
                <span className="text-base font-bold text-gray-900">
                  {hotel.min_price.toLocaleString("ru-RU")} ₽
                </span>
                <span className="text-xs text-gray-400"> / ночь</span>
              </div>
            </div>
          )}
        </div>

        {/* Select button */}
        <button
          onClick={handleSelectClick}
          className="w-full py-2.5 rounded-full bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 active:bg-blue-800 transition-colors"
        >
          Выбрать
        </button>
      </div>
    </div>
  );
}
